// Owners, review intervals, verification and review requests for one section's records
import { useCallback, useEffect, useRef, useState } from "react"
import { supabase } from "@/lib/agents/db"
import { loadRecordMeta, saveRecordMeta, type MetaMap, type Person, type RecordMeta } from "./persistence/recordMeta"
import type { PendingArea } from "./persistence/staging"

// How often live content is checked, unless a record sets its own
export const DEFAULT_REVIEW_MONTHS = 6
// Shown as "due soon" this long before the date
const DUE_SOON_DAYS = 30
const MONTH_MS = 30.44 * 24 * 3600 * 1000

export function useRecordMeta(team: string, area: PendingArea, author?: { name?: string; email?: string }) {
  const [meta, setMeta] = useState<MetaMap>({})
  const [available, setAvailable] = useState(false)
  const metaRef = useRef(meta)
  metaRef.current = meta
  const authorRef = useRef(author)
  authorRef.current = author

  useEffect(() => {
    let cancelled = false
    setMeta({})
    setAvailable(false)
    if (!team) return
    loadRecordMeta(team, area).then(
      m => { if (!cancelled) { setMeta(m); setAvailable(true) } },
      e => console.warn(`[smart-cms] record details didn't load: ${e?.message ?? e}`),
    )
    return () => { cancelled = true }
  }, [team, area])

  // Merges into the record's details; undefined fields are removed
  const update = useCallback((key: string, patch: Partial<RecordMeta>) => {
    const merged: RecordMeta = { ...metaRef.current[key], ...patch }
    for (const k of Object.keys(merged) as (keyof RecordMeta)[]) if (merged[k] === undefined) delete merged[k]
    const next = { ...metaRef.current, [key]: merged }
    metaRef.current = next
    setMeta(next)
    void saveRecordMeta(team, area, key, merged, authorRef.current)
  }, [team, area])

  const clearReview = useCallback((key: string) => {
    if (metaRef.current[key]?.review) update(key, { review: undefined })
  }, [update])

  // Includes updates React hasn't rendered yet
  const current = useCallback(() => metaRef.current, [])

  return { meta, available, update, clearReview, current }
}

export type RecordMetaStore = ReturnType<typeof useRecordMeta>

// The team's members, for owners and reviewers
export function useTeamMembers(team: string): Person[] {
  const [people, setPeople] = useState<Person[]>([])
  useEffect(() => {
    let cancelled = false
    setPeople([])
    if (!team) return
    void (supabase as any)
      .from("user_teams")
      .select("users!user_teams_user_id_fkey(id, email, first_name, last_name)")
      .eq("team_id", team)
      .then(({ data, error }: any) => {
        if (cancelled || error || !data) return
        // A person can be on the team twice; list them once
        const byId = new Map<string, Person>()
        for (const u of data.map((r: any) => r.users).filter(Boolean)) {
          byId.set(u.id, { id: u.id, name: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || "Unnamed" })
        }
        setPeople([...byId.values()].sort((a, b) => a.name.localeCompare(b.name)))
      })
    return () => { cancelled = true }
  }, [team])
  return people
}

export interface Freshness {
  // When someone last saved or confirmed the record
  checkedAt: number
  dueAt: number
  state: "ok" | "soon" | "overdue"
  months: number
}

// Saving a record counts as checking it, as does "Mark as verified"
export function freshnessOf(entity: { updatedAt?: number } | undefined, meta: RecordMeta | undefined, now = Date.now()): Freshness | null {
  if (!entity) return null
  const months = meta?.reviewEveryMonths ?? DEFAULT_REVIEW_MONTHS
  if (months <= 0) return null
  const checkedAt = Math.max(Number(entity.updatedAt) || 0, meta?.verifiedAt ?? 0)
  const dueAt = checkedAt + months * MONTH_MS
  const state = dueAt < now ? "overdue" : dueAt - now < DUE_SOON_DAYS * 24 * 3600 * 1000 ? "soon" : "ok"
  return { checkedAt, dueAt, state, months }
}

export function ago(ms: number, now = Date.now()): string {
  const days = Math.round((now - ms) / (24 * 3600 * 1000))
  if (days < 1) return "today"
  if (days < 45) return `${days} day${days === 1 ? "" : "s"} ago`
  const months = Math.round(days / 30.44)
  if (months < 18) return `${months} months ago`
  return `${Math.round(months / 12)} years ago`
}

export function inTime(ms: number, now = Date.now()): string {
  const days = Math.round((ms - now) / (24 * 3600 * 1000))
  if (days <= 0) return "now"
  if (days < 45) return `in ${days} day${days === 1 ? "" : "s"}`
  return `in ${Math.round(days / 30.44)} months`
}
